import { alpha, styled } from '@mui/material/styles'
import ReactCalendar from 'react-calendar'

export const StyledCalendar = styled(ReactCalendar)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  transition: theme.transitions.create('box-shadow'),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,

  '& .react-calendar__navigation': {
    display: 'flex',
    padding: theme.spacing(2, 0),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

    '& button': {
      ...theme.typography.h6,
      fontWeight: 600,
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1, 2),
      cursor: 'pointer',
      transition: theme.transitions.create(['background-color', 'color']),

      '&:enabled:hover, &:enabled:focus': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main,
      },

      '&:disabled': {
        color: theme.palette.text.disabled,
      },
    },
  },

  '& .react-calendar__month-view__weekdays': {
    textAlign: 'center',
    backgroundColor: alpha(theme.palette.background.default, 0.5),
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 0),
    marginBottom: theme.spacing(1),

    '& .react-calendar__month-view__weekdays__weekday': {
      padding: theme.spacing(1),

      '& abbr': {
        textDecoration: 'none',
        ...theme.typography.subtitle2,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
    },
  },

  '& .react-calendar__month-view__days': {
    '& .react-calendar__tile': {
      position: 'relative',
      minHeight: '7.5rem',
      padding: theme.spacing(1),
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      transition: theme.transitions.create(['background-color', 'border-color']),

      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        borderColor: alpha(theme.palette.primary.main, 0.2),
      },

      '& abbr': {
        ...theme.typography.subtitle1,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textDecoration: 'none',
      },

      '&.react-calendar__tile--now': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        borderColor: alpha(theme.palette.primary.main, 0.3),

        '& abbr': {
          color: theme.palette.primary.main,
          fontWeight: 700,
        },
      },

      '&.react-calendar__tile--neighboringMonth': {
        backgroundColor: alpha(theme.palette.background.default, 0.3),

        '& abbr': {
          color: theme.palette.text.disabled,
          fontWeight: 400,
        },
      },

      '&.react-calendar__tile--active': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: theme.palette.primary.main,

        '& abbr': {
          color: theme.palette.primary.main,
        },
      },
    },
  },

  '& .react-calendar__year-view': {
    '& .react-calendar__year-view__months': {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing(2),
      padding: theme.spacing(2, 0),
    },

    '& .react-calendar__tile': {
      backgroundColor: alpha(theme.palette.background.default, 0.5),
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      borderRadius: theme.spacing(2),
      padding: theme.spacing(2),
      minHeight: '3.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: theme.transitions.create([
        'background-color',
        'border-color',
        'transform',
      ]),

      '& abbr': {
        ...theme.typography.subtitle1,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textDecoration: 'none',
      },

      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: alpha(theme.palette.primary.main, 0.3),
        transform: 'translateY(-2px)',
      },

      '&.react-calendar__tile--active': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,

        '& abbr': {
          color: 'white',
          fontWeight: 700,
        },
      },

      '&.react-calendar__tile--now': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15),
        borderColor: theme.palette.primary.main,

        '& abbr': {
          color: theme.palette.primary.main,
          fontWeight: 700,
        },
      },
    },
  },

  '& .react-calendar__decade-view': {
    '& .react-calendar__decade-view__years': {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing(1.5),
      padding: theme.spacing(2, 0),
    },

    '& .react-calendar__tile': {
      backgroundColor: alpha(theme.palette.background.default, 0.5),
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      borderRadius: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      minHeight: '3rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: theme.transitions.create([
        'background-color',
        'border-color',
        'transform',
      ]),

      '& abbr': {
        ...theme.typography.body1,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textDecoration: 'none',
      },

      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: alpha(theme.palette.primary.main, 0.3),
        transform: 'scale(1.05)',
      },

      '&.react-calendar__tile--active': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,

        '& abbr': {
          color: 'white',
          fontWeight: 700,
        },
      },

      '&.react-calendar__tile--now': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15),
        borderColor: theme.palette.primary.main,

        '& abbr': {
          color: theme.palette.primary.main,
          fontWeight: 700,
        },
      },
    },
  },

  '& .react-calendar__century-view': {
    '& .react-calendar__century-view__decades': {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing(1.5),
      padding: theme.spacing(2, 0),
    },

    '& .react-calendar__tile': {
      backgroundColor: alpha(theme.palette.background.default, 0.5),
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      borderRadius: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      minHeight: '3rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: theme.transitions.create([
        'background-color',
        'border-color',
        'transform',
      ]),

      '& abbr': {
        ...theme.typography.body1,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textDecoration: 'none',
      },

      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: alpha(theme.palette.primary.main, 0.3),
        transform: 'scale(1.05)',
      },

      '&.react-calendar__tile--active': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,

        '& abbr': {
          color: 'white',
          fontWeight: 700,
        },
      },
    },
  },

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),

    '& .react-calendar__month-view__days .react-calendar__tile': {
      minHeight: '5rem',
      fontSize: '0.8rem',
    },

    '& .react-calendar__year-view .react-calendar__year-view__months': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },

    '& .react-calendar__decade-view .react-calendar__decade-view__years': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },

    '& .react-calendar__century-view .react-calendar__century-view__decades': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
}))
